import type { TaxApiClient } from "~/lib/api/client";
import { tokenStore } from "~/lib/auth/token-store";
import { isSessionReplayAllowed } from "./enabled";
import {
  flushSignalsOnExit,
  notifyReplayRoute,
  startSessionRecorder,
  stopSessionRecorder,
} from "./recorder";
import { currentReplaySessionId } from "./session-id";
import { installReplaySignals, recordNetworkFailure } from "./signals";

export { isSessionReplayAllowed } from "./enabled";
export { REPLAY_BLOCK_SELECTOR, REPLAY_MASK_SELECTOR } from "./sensitive";
export { recordNetworkFailure } from "./signals";
export { getReplayConfig } from "./recorder";

type RouterLike = {
  subscribe: (
    event: "onResolved",
    // TanStack passes a rich event; we only need the resolved pathname.
    listener: (event: { toLocation: { pathname: string } }) => void,
  ) => () => void;
  state: { location: { pathname: string } };
};

type InstallOptions = {
  api: TaxApiClient;
  router: RouterLike;
  buildVersion?: string;
};

function scheduleIdle(work: () => void): () => void {
  if (typeof window === "undefined") {
    work();
    return () => undefined;
  }
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(() => work(), { timeout: 4_000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(work, 2_000);
  return () => window.clearTimeout(id);
}

/** Header value for `x-tax-session-id` when recording is allowed. */
export function replaySessionHeader(): string | null {
  if (!isSessionReplayAllowed()) return null;
  try {
    if (!tokenStore.hasToken()) return null;
    return currentReplaySessionId();
  } catch {
    return null;
  }
}

/**
 * Install signals immediately and schedule the rrweb recorder after the
 * authenticated shell is idle. Returns a disposer for unmount / sign-out.
 */
export function installSessionReplay(options: InstallOptions): () => void {
  if (!isSessionReplayAllowed()) return () => undefined;

  const disposeSignals = installReplaySignals({
    subscribe: (event, listener) =>
      options.router.subscribe(event, (payload) => {
        listener({
          toLocation: {
            pathname: payload.toLocation.pathname,
          },
        });
        notifyReplayRoute(payload.toLocation.pathname);
      }),
  });

  let disposeRecorder: (() => void) | null = null;
  let cancelIdle: (() => void) | null = null;
  let stopped = false;

  // root-layout installs this only for an authenticated session and disposes it on sign-out,
  // so no stored-token check here (mock auth keeps no token).
  const start = () => {
    if (stopped) return;
    disposeRecorder?.();
    disposeRecorder = startSessionRecorder({
      api: options.api,
      buildVersion: options.buildVersion,
      getPathname: () => options.router.state.location.pathname,
    });
  };

  cancelIdle = scheduleIdle(start);

  // Until the idle start runs, an exit (reload, closing the tab) still flushes the signals.
  const onExit = () => flushSignalsOnExit(options.api);
  const onVisibility = () => {
    if (document.visibilityState === "hidden") onExit();
  };
  window.addEventListener("pagehide", onExit);
  document.addEventListener("visibilitychange", onVisibility);

  const unsubscribeAuth = tokenStore.subscribe((hasToken) => {
    if (!hasToken) {
      disposeRecorder?.();
      disposeRecorder = null;
      stopSessionRecorder();
      return;
    }
    cancelIdle?.();
    cancelIdle = scheduleIdle(start);
  });

  return () => {
    stopped = true;
    window.removeEventListener("pagehide", onExit);
    document.removeEventListener("visibilitychange", onVisibility);
    cancelIdle?.();
    cancelIdle = null;
    unsubscribeAuth();
    disposeRecorder?.();
    disposeRecorder = null;
    stopSessionRecorder();
    disposeSignals();
  };
}

/** Transport hook: record network failures that never got an API answer. */
export function onReplayNetworkFailure(info: {
  requestId: string;
  method: string;
  path: string;
}): void {
  if (!isSessionReplayAllowed()) return;
  recordNetworkFailure(info);
}
